import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { makeStyles, tokens, Text, Card, Title1, Title3 } from '@fluentui/react-components';
import { Person24Regular } from '@fluentui/react-icons';
import useStore from '../store';
import { useScrollManager } from '../hooks/useScrollManager';

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '100%',
        minHeight: '100vh',
        backgroundColor: tokens.colorNeutralBackground2,
    },
    header: {
        position: 'relative',
        height: '200px',
        '@media (min-width: 768px)': {
            height: '300px',
        },
    },
    headerContent: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.9) 100%)',
        padding: tokens.spacingVerticalL,
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        '@media (min-width: 768px)': {
            flexDirection: 'row',
            alignItems: 'flex-end',
            gap: tokens.spacingHorizontalXL,
        },
    },
    actorImage: {
        width: '120px',
        height: '120px',
        borderRadius: '50%',
        objectFit: 'cover',
        border: `4px solid ${tokens.colorNeutralBackground1}`,
        background: tokens.colorNeutralBackground3,
        marginBottom: tokens.spacingVerticalM,
    },
    mainContent: {
        position: 'relative',
        padding: tokens.spacingVerticalM,
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalL,
        '@media (min-width: 768px)': {
            padding: tokens.spacingVerticalXL,
            paddingLeft: tokens.spacingHorizontalXXL,
            paddingRight: tokens.spacingHorizontalXXL,
        },
    },
    mediaGrid: {
        marginTop: tokens.spacingVerticalM,
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        gap: tokens.spacingHorizontalL,
    },
    mediaCard: {
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: tokens.colorNeutralBackground1,
        borderRadius: tokens.borderRadiusMedium,
        boxShadow: tokens.shadow4,
        padding: tokens.spacingVerticalM,
        gap: tokens.spacingVerticalM,
        minWidth: 0,
    },
    mediaHeader: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: tokens.spacingHorizontalM,
        marginBottom: tokens.spacingVerticalS,
        textDecoration: 'none',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        ':hover': {
            opacity: 0.8,
        },
    },
    mediaCover: {
        width: '48px',
        height: '68px',
        borderRadius: tokens.borderRadiusSmall,
        objectFit: 'cover',
        background: tokens.colorNeutralBackground3,
        flexShrink: 0,
    },
    mediaInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXXS,
        flex: 1,
        minWidth: 0,
    },
    mediaTitle: {
        color: tokens.colorNeutralForeground1,
        fontWeight: tokens.fontWeightSemibold,
        fontSize: tokens.fontSizeBase400,
        lineHeight: tokens.lineHeightBase400,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    mediaStatus: {
        alignItems: 'center',
        gap: tokens.spacingHorizontalXS,
        color: tokens.colorNeutralForeground2,
        fontSize: tokens.fontSizeBase200,
    },
    charactersList: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
    },
    characterRow: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalM,
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusSmall,
        padding: tokens.spacingVerticalXS,
    },
    characterImage: {
        width: '48px',
        height: '48px',
        borderRadius: tokens.borderRadiusSmall,
        objectFit: 'cover',
        background: tokens.colorNeutralBackground3,
    },
    characterInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXXS,
        minWidth: 0,
    },
    characterName: {
        color: tokens.colorNeutralForeground1,
        fontWeight: tokens.fontWeightSemibold,
        fontSize: tokens.fontSizeBase400,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    characterRole: {
        color: tokens.colorBrandForeground1,
        fontSize: tokens.fontSizeBase200,
        fontWeight: tokens.fontWeightSemibold,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    },
});

export const VoiceActorPage: React.FC = () => {
    const styles = useStyles();
    const { id } = useParams<{ id: string }>();
    const userMediaList = useStore(s => s.userMediaList);
    const navigate = useNavigate();
    const setSelectedMediaId = useStore(state => state.setSelectedMediaId);
    const { saveScrollPosition } = useScrollManager();

    // Group roles by media
    const mediaRoles = React.useMemo(() => {
        const grouped: Record<string, {
            mediaId: number;
            mediaTitle: string;
            mediaCover: string;
            userStatus: string;
            userScore: number | null;
            characters: {
                id: number;
                name: string;
                imageUrl: string | null;
                role: string;
            }[];
        }> = {};
        userMediaList.forEach(media => {
            if (media.userStatus === 'PLANNING' || media.userStatus === 'DROPPED' || media.userStatus === 'PAUSED')
                return;

            const chars = media.characters.filter(
                char => char.voiceActor && String(char.voiceActor.id) === id
            );
            if (chars.length > 0) {
                grouped[media.id] = {
                    mediaId: media.id,
                    mediaTitle: media.title.english || media.title.romaji || '',
                    mediaCover: media.coverImage?.medium || media.coverImage?.large || '',
                    userStatus: media.userStatus,
                    userScore: media.userScore,
                    characters: chars.map(char => ({
                        id: char.id,
                        name: char.name,
                        imageUrl: char.imageUrl,
                        role: char.role,
                    })),
                };
            }
        });
        return Object.values(grouped);
    }, [userMediaList, id]);

    const handleClick = (id: number) => {
        // Save current scroll position before navigating
        saveScrollPosition();

        setSelectedMediaId(id);
        navigate(`/media/${id}`);
    };

    // Get the voice actor info from the first found role
    const actor = React.useMemo(() => {
        for (const media of userMediaList) {
            for (const char of media.characters) {
                if (char.voiceActor && String(char.voiceActor.id) === id) {
                    return char.voiceActor;
                }
            }
        }
        return null;
    }, [userMediaList, id]);

    if (!actor) {
        return (
            <div className={styles.container}>
                <Title1>Voice Actor Not Found</Title1>
                <Text>No information available for this voice actor.</Text>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.headerContent}>
                    {actor.imageUrl && (
                        <img src={actor.imageUrl} alt={actor.name || 'Voice Actor'} className={styles.actorImage} />
                    )}
                    <Title1>{actor.name}</Title1>
                </div>
            </div>
            <div className={styles.mainContent}>
                <Title3>Anime & Characters</Title3>
                <div className={styles.mediaGrid}>
                    {mediaRoles.map(media => (
                        <Card key={media.mediaId} className={styles.mediaCard} onClick={() => handleClick(media.mediaId)}>
                            <div className={styles.mediaHeader}>
                                {media.mediaCover ? (
                                    <img
                                        src={media.mediaCover}
                                        alt={media.mediaTitle}
                                        className={styles.mediaCover}
                                    />
                                ) : (
                                    <div className={styles.mediaCover}>
                                        <Person24Regular />
                                    </div>
                                )}
                                <div className={styles.mediaInfo}>
                                    <Text className={styles.mediaTitle}>{media.mediaTitle}</Text>
                                    <div className={styles.mediaStatus}>
                                        <Text block>{media.userStatus}</Text>
                                        {media.userScore && (
                                            <Text block>Score: {media.userScore}</Text>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className={styles.charactersList}>
                                {media.characters.map(character => (
                                    <div key={character.id} className={styles.characterRow}>
                                        {character.imageUrl ? (
                                            <img
                                                src={character.imageUrl}
                                                alt={character.name}
                                                className={styles.characterImage}
                                            />
                                        ) : (
                                            <div className={styles.characterImage}>
                                                <Person24Regular />
                                            </div>
                                        )}
                                        <div className={styles.characterInfo}>
                                            <Text className={styles.characterName}>{character.name}</Text>
                                            <Text className={styles.characterRole}>{character.role}</Text>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    ))}
                </div>
            </div>
        </div>
    );
};
